import React, { useState, useEffect } from 'react';
import buttonImg from './button.png'; // Import the button image
import KeywordModal from './components/KeywordModal';
import FinalAgentModal from './components/FinalAgentModal'; // Import the new modal
import placeholdersData from './SelectPlaceholders.json'; // Import the placeholders from JSON

const useCases = {
  "Custom Use Case": {
    description: "Define your own use case for the agent.",
    example: "I want an agent that generates creative writing prompts for fiction authors, offering imaginative ideas across various genres.",
    instructions: ""
  },
  "Employee Self Service": {
    description: "An agent for employees to get information about company policies, benefits, and more, with a friendly and helpful tone.",
    example: "I need an agent that helps employees understand their health benefits, answering questions in a friendly way and referencing the employee handbook.",
    instructions: "You are an employee self-service assistant. Use a friendly, conversational tone when addressing employees. Start by asking how you can help with company policies, benefits, or other HR-related questions. Provide detailed information about employee benefits, time-off policies, and workplace procedures. Reference the employee handbook when appropriate. Keep responses confidential and suggest connecting with HR for sensitive or complex issues. End interactions by asking if there's anything else you can assist with."
  },
  "Business Operations": {
    description: "An agent to assist with internal business processes, such as inventory management or workflow optimization, with a focus on efficiency.",
    example: "Create an agent to streamline inventory tracking, providing clear steps and focusing on operational efficiency.",
    instructions: "You are a business operations assistant. Focus on providing efficient solutions and actionable insights. When addressing workflow questions, offer step-by-step guides and best practices for optimization. For inventory management, help with tracking systems, reordering processes, and inventory analysis. Use a direct, professional tone and emphasize efficiency in all recommendations. Suggest automation opportunities when relevant. Ask clarifying questions if the request is unclear to ensure you're providing the most useful information."
  },
  "Customer Support": {
    description: "An agent to handle customer inquiries and support tickets, responding professionally and providing links to documentation when possible.",
    example: "Create an agent to assist customers with software troubleshooting, maintaining a professional tone and linking to relevant help articles.",
    instructions: "You are a customer support agent. Always maintain a professional and helpful tone. Begin by greeting the customer and asking how you can help. Address customers respectfully and acknowledge their concerns. Provide clear, concise solutions and link to relevant documentation when possible. If you don't know the answer, say so honestly and offer to escalate the issue to a human agent. End interactions with {{username}} by asking if there's anything else you can assist them with."
  }
};

const availableCapabilities = ["Code Interpreter", "Image Generation", "Data Analysis"];

// Load placeholders from the imported JSON data
const availablePlaceholders = placeholdersData.availablePlaceholders.map(placeholder => ({
  name: placeholder.name,
  description: placeholder.description
}));

function AgentForm({ onGenerate }) {
  const [useCase, setUseCase] = useState("Custom Use Case");
  const [name, setName] = useState("");
  const [description, setDescription] = useState(useCases["Customer Support"].description);
  const [instructions, setInstructions] = useState("");
  const [knowledgeSourceType, setKnowledgeSourceType] = useState("SharePoint");
  const [knowledgeSourceValue, setKnowledgeSourceValue] = useState("");
  const [knowledgeSourcePills, setKnowledgeSourcePills] = useState([]);
  const [knowledgeSourceError, setKnowledgeSourceError] = useState("");
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const [instructionsTextareaRef, setInstructionsTextareaRef] = useState(null);
  const [isKeywordModalOpen, setIsKeywordModalOpen] = useState(false);
  const [isGeneratingInstructions, setIsGeneratingInstructions] = useState(false);
  const [isFinalModalOpen, setIsFinalModalOpen] = useState(false); // State for modal visibility
  const [finalAgentData, setFinalAgentData] = useState(null); // State for modal data
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(true);
  // const [isGeneratingName, setIsGeneratingName] = useState(false);

  useEffect(() => {
    if (useCase === "Custom Use Case") {
      setDescription("");
      setInstructions("");
    } else {
      setDescription(useCases[useCase].description);
      setInstructions(useCases[useCase].instructions);
    }
  }, [useCase]);

  const validateSharePoint = (url) => {
    try {
      const u = new URL(url);
      return u.protocol === 'https:' && u.hostname.includes('sharepoint.com');
    } catch {
      return false;
    }
  };

  const validateWebURL = (url) => {
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const validateDocument = (doc) => {
    return typeof doc === 'string' && doc.trim().length > 0;
  };

  const validateKnowledgeSource = (type, value) => {
    if (!value.trim()) return "Value required.";
    switch (type) {
      case 'SharePoint':
        return validateSharePoint(value) ? null : 'Invalid SharePoint URL.';
      case 'Web URL':
        return validateWebURL(value) ? null : 'Invalid Web URL.';
      case 'Document':
        return validateDocument(value) ? null : 'Document path/name required.';
      default:
        return "Unknown type.";
    }
  };

  const handleAddKnowledgeSource = (e) => {
    e.preventDefault();
    const error = validateKnowledgeSource(knowledgeSourceType, knowledgeSourceValue);
    if (error) {
      setKnowledgeSourceError(error);
      return;
    }
    if (
      knowledgeSourcePills.some(
        (ks) => ks.type === knowledgeSourceType && ks.value.trim() === knowledgeSourceValue.trim()
      )
    ) {
      setKnowledgeSourceError("This knowledge source is already added.");
      return;
    }
    setKnowledgeSourcePills([
      ...knowledgeSourcePills,
      { type: knowledgeSourceType, value: knowledgeSourceValue.trim() }
    ]);
    setKnowledgeSourceValue("");
    setKnowledgeSourceError("");
  };

  const handleRemoveKnowledgeSource = (index) => {
    setKnowledgeSourcePills(knowledgeSourcePills.filter((_, i) => i !== index));
  };

  const insertPlaceholder = (placeholder) => {
    if (!instructionsTextareaRef) return;
    
    const textarea = instructionsTextareaRef;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    const currentText = instructions;
    const newText = currentText.substring(0, start) + placeholder + currentText.substring(end);
    
    setInstructions(newText);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  const highlightPlaceholders = (text) => {
    if (!text) return [];
    
    const placeholderPattern = /\{\{(\w+)\}\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = placeholderPattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      parts.push(
        <span key={match.index} className="bg-blue-100 text-blue-800 px-1 rounded">
          {match[0]}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // 1. Validation
    if (!name || !description || !instructions) {
      alert("Please fill out the Agent Name, the Description and Instruction before pushing the Generate Agent Configuration button.");
      return;
    }
    if (name.length > 30) {
      alert("Name must be 30 characters or less.");
      return;
    }
    if (description.length > 1000) {
      alert("Description must be 1000 characters or less.");
      return;
    }
     if (instructions.length > 8000) { // Assuming max length based on textarea
       alert("Instructions must be 8000 characters or less.");
       return;
     }

    // 2. Check for knowledge source errors (validation is already done on add, but double check)
    const hasKnowledgeSourceError = knowledgeSourcePills.some(
        (ks) => validateKnowledgeSource(ks.type, ks.value)
    );
    if (hasKnowledgeSourceError) {
      alert("Please fix errors in the Knowledge Sources section before generating.");
      setKnowledgeSourceError("Please resolve errors in added sources."); // Optionally highlight again
      return;
    }


    // 3. Prepare data for the modal
    const agentData = {
      name,
      description,
      instructions,
      knowledgeSourcePills,
      selectedCapabilities
    };

    // 4. Set modal data and open modal
    setFinalAgentData(agentData);
    setIsFinalModalOpen(true);

    // Note: The original onGenerate(outputText) is replaced by opening the modal.
    // If you still need to *send* this data somewhere *after* the modal is shown/closed,
    // you might adjust the logic here or in the modal's onClose handler.
  };

  const handleDescriptionGenerated = (generatedDescription) => {
    setDescription(generatedDescription);
  };

  const handleClear = () => {
    setUseCase("Custom Use Case");
    setName("");
    setDescription("");
    setInstructions("");
    setKnowledgeSourceType("SharePoint");
    setKnowledgeSourceValue("");
    setKnowledgeSourcePills([]);
    setKnowledgeSourceError("");
    setSelectedCapabilities([]);
    setIsPreviewExpanded(true);
  };

  const descriptionSection = (
    <div className="mb-4">
      <div className="py-2"></div>
      <div class="grid grid-cols-[1fr_2fr_1fr] gap-4">
        <div class="..."><label className="block text-gray-700 mb-2" htmlFor="description">Description:</label></div>
        <div class="justify-self-center ...">
          <button
            type="button"
            onClick={() => setIsKeywordModalOpen(true)}
            className="text-blue-500 hover:text-blue-700 text-sm"
          >
            Generate with Keywords
          </button>
        </div>
        <div class="justify-self-end ...">
          <button type="button" className="ml-2 p-1 bg-transparent border-none" title="Generate Description Button" aria-label="Generate Description Button">
            <img src={buttonImg} alt="Generate Description" className="w-6 h-6" />
          </button>
        </div>
      </div>
      <div className="flex items-center">
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1000}
          className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
          rows="3"
        ></textarea>
      </div>
      <p className="text-sm text-gray-500 mt-1">
          {description.length}/1000 characters
      </p>
    </div>
  );

  return (
    <>
      <section id="agent-generator" className="bg-white rounded-lg p-6 max-w-2xl mx-auto shadow-[0px_0px_10px_rgba(0,0,0,0.9)]">
        <h3 className="text-2xl font-semibold text-gray-800 mb-4">Create Your Agent</h3>
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="useCase">Use Case</label>
            <select
              id="useCase"
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
            >
              {Object.keys(useCases).map(uc => (
                <option key={uc}>{uc}</option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">{useCases[useCase].description}</p>
          </div>
          <div className="py-1"></div>
          <div className="mb-4">
            <div class="grid grid-cols-3 gap-4">
              <div class="justify-self-start ..."><label className="block text-gray-700 mb-2" htmlFor="name">Agent Name: </label></div>
              <div class="...">
                <label 
                  className="ml-2 text-blue-500 hover:text-blue-700 text-sm cursor-pointer"
                  onClick={() => {
                    if (!description) {
                      alert("Please enter a description first.");
                    } else {
                      // Call the LLM to generate a name based on the description
                      const systemPrompt = `You are an expert at generating concise, professional, safe-for-work names for Microsoft Copilot agents. Create a name of up to three words based on the provided description text. The name must be corporately safe, suitable for a professional workplace, and reflect the agent's key themes, functionalities, or purpose. Ensure the name is succinct, relevant, professional, and aligns with Microsoft Copilot's corporate standards, avoiding inappropriate terms. If the description is vague, prioritize clarity and neutrality. Use the following description text to generate the agent name:\n\n${description}`;

                      const requestBody = JSON.stringify({
                        task: 'Generate Name',
                        context: systemPrompt
                      });
                      console.log('Sending request to API:', {
                        endpoint: 'https://api2.prompteh.com/',
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-API-Key': process.env.REACT_APP_API_KEY ? '[API_KEY_SET]' : '[API_KEY_NOT_SET]'
                        },
                        body: requestBody
                      }); 

                      fetch('https://api2.prompteh.com/', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-API-Key': process.env.REACT_APP_API_KEY
                        },
                        body: requestBody
                      })
                      .then(response => {
                        if (!response.ok) {
                          return response.text().then(errorText => {
                            throw new Error(`Error: ${response.status} ${response.statusText}`);
                          });
                        }
                        return response.json();
                      })
                      .then(data => {
                        setName(data.name);
                      })
                      .catch(error => {
                        console.error('Error generating name:', error);
                        alert('Failed to generate name. Please try again.');
                      });
                    }
                  }}
                >
                  <span title="Clicking this link will generate an Agent Name based on the current description">
                    Auto-generate name
                  </span>
                </label>
              </div>
              <div class="justify-self-end ...">
                <button
                  type="button"
                  className="ml-2 p-1 bg-transparent border-none"
                  title="Agent Name Button"
                  aria-label="Agent Name Button"
                >
                  <img src={buttonImg} alt="Generate Name" className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <textarea
                rows="1"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={30}
                className="flex-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
              ></textarea>
            </div>
            <p className="text-sm text-gray-500 mt-1">
                {name.length}/30 characters
            </p>
          </div>

          {descriptionSection}

          <div className="mb-4">
            <div className="py-2"></div>
            <div class="grid grid-cols-3 gap-4">
            <div class="justify-self-start ...">
              <label className="block text-gray-700 mb-2" htmlFor="instructions">
                Instructions: 
              </label>
            </div>
            <div class="justify-self-left ...">
              <button
                  type="button"
                  onClick={() => {
                    if (!name || !description) {
                      alert("Please provide both an Agent Name and Description first.");
                      return;
                    }

                    const systemPrompt = `You are an expert at generating instructions for Microsoft Copilot agents. Always respond with a JSON object containing a single field "instructions" with the generated instructions as a string.You will generate instructions for a Microsoft Copilot Agent based on the name and description provided below.The instructions must be professional, clear, actionable, and concise.The instructions will have a minimum of 500 characters and a maximum length of 4500 characters.They should effectively guide the agent's behavior, tone, and response scope. Structure the instructions to cover: 
                    The agent's primary function and purpose
                    The appropriate language and tone for interactions
                    Specific areas of focus or expertise
                    Guidelines for handling queries outside its scope
                    Any required formats or structures for responses
                    
                    Use the provided name and description to tailor the instructions to the agent's specific role. If the name or description is vague, make reasonable inferences to create relevant instructions. Prioritize clarity and brevity while maintaining a professional tone consistent with Microsoft Copilot's corporate standards.
                    
                    The Name and Description of the new Copilot Agent are:
                    Name: ${name}
                    Description: ${description}`;

                    const requestBody = JSON.stringify({
                      task: 'Generate Instructions',
                      context: systemPrompt
                    });

                    // Create AbortController for timeout
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => {
                        console.warn('Request timed out, aborting fetch.');
                        controller.abort();
                    }, 120000); // 120 seconds timeout

                    // Set loading state for instructions generation
                    setIsGeneratingInstructions(true);
                    setInstructions(""); // Clear previous instructions
                    
                    console.log('Sending request to API for generating instructions:', {
                      endpoint: 'https://api2.prompteh.com/',
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-API-Key': process.env.REACT_APP_API_KEY ? '[API_KEY_SET]' : '[API_KEY_NOT_SET]'
                      },
                      body: requestBody
                    });

                    fetch('https://api2.prompteh.com/', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-API-Key': process.env.REACT_APP_API_KEY
                      },
                      body: requestBody,
                      signal: controller.signal
                    })
                    .then(response => {
                      clearTimeout(timeoutId); // Clear timeout as soon as headers are received
                      
                      const contentType = response.headers.get('Content-Type');
                      console.log('API Response Content-Type:', contentType);

                      // Check for non-OK status first, even for streams
                      if (!response.ok) {
                        // Attempt to parse error as JSON, then fallback to text
                        return response.text().then(errorText => {
                          let errorMessage = `Error: ${response.status} ${response.statusText}`;
                          try {
                            const errorJson = JSON.parse(errorText);
                            if (errorJson.error && errorJson.error.message) {
                              errorMessage = `Error ${errorJson.error.code || response.status}: ${errorJson.error.message}`;
                            }
                          } catch (e) {
                            // Not JSON, or malformed, use the text directly if short
                            if (errorText.length < 200) errorMessage = errorText;
                          }
                          console.error('API Error response content:', errorText);
                          throw new Error(errorMessage);
                        });
                      }
                      
                      if (contentType && contentType.includes('text/event-stream') && response.body) {
                        console.log('Processing response as text/event-stream.');
                        const reader = response.body.getReader();
                        const decoder = new TextDecoder();
                        let buffer = '';

                        const processStream = async () => {
                          try {
                            while (true) {
                              const { done, value } = await reader.read();
                              if (done) {
                                console.log('Stream finished.');
                                // Process any final data in buffer if stream ends abruptly without \n\n
                                if (buffer.startsWith('data: ')) {
                                    const jsonString = buffer.substring(5).trim();
                                    if (jsonString) {
                                        try {
                                            const parsedChunk = JSON.parse(jsonString);
                                            // Simplified: Assuming final buffer chunk might also contribute content
                                            let finalChunkContent = null;
                                            if (parsedChunk.instructions !== undefined) {
                                                finalChunkContent = parsedChunk.instructions;
                                            } else if (parsedChunk.choices && parsedChunk.choices[0].delta && parsedChunk.choices[0].delta.content) {
                                                finalChunkContent = parsedChunk.choices[0].delta.content;
                                            } else if (parsedChunk.content) {
                                                finalChunkContent = parsedChunk.content;
                                            }
                                            if (finalChunkContent) {
                                                setInstructions(prev => prev + finalChunkContent);
                                            }
                                        } catch (e) { /* ignore final parse error on buffer */ }
                                    }
                                }

                                // Attempt to clean up the instructions state if it's a JSON string
                                setInstructions(currentStreamedInstructions => {
                                    let finalOutput = currentStreamedInstructions;
                                    if (typeof currentStreamedInstructions === 'string' &&
                                        currentStreamedInstructions.trim().startsWith('{"instructions":') &&
                                        currentStreamedInstructions.trim().endsWith('}')) {
                                        try {
                                            const parsed = JSON.parse(currentStreamedInstructions);
                                            if (parsed && typeof parsed.instructions === 'string') {
                                                finalOutput = parsed.instructions;
                                                console.log("Frontend cleanup: Parsed instructions from JSON string.");
                                            }
                                        } catch (e) {
                                            console.warn("Frontend cleanup: Streamed content looked like JSON but failed to parse, using as is:", e);
                                        }
                                    }
                                    return finalOutput;
                                });

                                setIsGeneratingInstructions(false);
                                break;
                              }

                              buffer += decoder.decode(value, { stream: true });
                              let eolIndex;
                              // SSE messages are separated by double newlines (\n\n)
                              while ((eolIndex = buffer.indexOf('\n\n')) >= 0) {
                                const line = buffer.substring(0, eolIndex).trim();
                                buffer = buffer.substring(eolIndex + 2); // Remainder for next iteration

                                if (line.startsWith('data: ')) {
                                  const jsonString = line.substring(5).trim();
                                  if (jsonString === '[DONE]') { 
                                      console.log('Stream indicated [DONE].');
                                      // No break here, let the reader.read() determine 'done' status
                                      // to ensure the cleanup logic in 'if (done)' runs reliably.
                                  }
                                  if (jsonString && jsonString !== '[DONE]') { // Ensure not to parse [DONE]
                                    try {
                                      const parsedChunk = JSON.parse(jsonString);
                                      
                                      // Check for multiple possible formats
                                      let chunkContent = null;
                                      
                                      // Format 1: Direct JSON with instructions field (what we're seeing from x.ai)
                                      if (parsedChunk.instructions !== undefined) {
                                        chunkContent = parsedChunk.instructions;
                                        console.log('Found instructions field in chunk');
                                      } 
                                      // Format 2: OpenAI-style delta format
                                      else if (parsedChunk.choices && parsedChunk.choices[0].delta && parsedChunk.choices[0].delta.content) {
                                        chunkContent = parsedChunk.choices[0].delta.content;
                                        console.log('Found delta content in chunk');
                                      }
                                      // Format 3: Check for a different field format
                                      else if (parsedChunk.content) {
                                        chunkContent = parsedChunk.content;
                                        console.log('Found content field in chunk');
                                      }
                                      // Format 4: Error message in chunk
                                      else if (parsedChunk.error) {
                                        console.error("Error message in stream chunk:", parsedChunk.error);
                                        chunkContent = `\n[Error from AI: ${parsedChunk.error.message || 'Unknown error'}]`;
                                      }
                                      
                                      // If we found content in any format, update the UI
                                      if (chunkContent !== null) {
                                        setInstructions(prev => prev + chunkContent);
                                      } else {
                                        console.warn('Unrecognized chunk format:', parsedChunk);
                                      }
                                    } catch (e) {
                                      console.error('Error parsing streamed JSON chunk:', e, "JSON String:", jsonString);
                                      // Potentially corrupted data, skip or log
                                    }
                                  }
                                }
                              }
                            }
                          } catch (streamError) {
                              console.error('Error reading from stream:', streamError);
                              setIsGeneratingInstructions(false);
                              if (streamError.name !== 'AbortError') { // AbortError is expected on timeout
                                alert('An error occurred while streaming instructions. Please try again.');
                              }
                          }
                        };
                        processStream();
                        return; // Streaming is being handled
                      } else {
                        // Fallback if not a stream or body is null, or if !response.ok was not caught above
                        console.warn('Response is not text/event-stream or body is null. Attempting to parse as JSON.');
                        return response.json().then(data => {
                          if (data && data.instructions) {
                            setInstructions(data.instructions);
                          } else if (data && data.error && data.error.message) {
                            console.error("Fallback JSON error:", data.error.message);
                            alert(`Failed to generate instructions: ${data.error.message}`);
                            setInstructions(`Error: ${data.error.message}`);
                          } 
                           else {
                            setInstructions("Failed to load instructions. The response format was unexpected.");
                          }
                          setIsGeneratingInstructions(false);
                        });
                      }
                    })
                    .catch(error => {
                      clearTimeout(timeoutId); // Ensure timeout is cleared on any error
                      setIsGeneratingInstructions(false);
                      if (error.name === 'AbortError') {
                        console.error('Instructions generation timed out by AbortController.');
                        alert('Request timed out. Please try again.');
                      } else {
                        console.error('Error generating instructions (fetch catch):', error);
                        alert(`Failed to generate instructions: ${error.message}. Please try again.`);
                      }
                    });
                  }}
                  className="text-blue-500 hover:text-blue-700 text-sm font-normal underline"
                  title="Auto-generate instructions based on Name and Description"
                  disabled={isGeneratingInstructions}
                >
                  {isGeneratingInstructions ? "Generating..." : "Auto Generate"}
              </button>
            </div>
            <div class="justify-self-end ...">
              <button type="button" className="ml-2 p-1 bg-transparent border-none"title="Generate Instructions Button" aria-label="Generate Instructions Button">
                <img src={buttonImg} alt="Generate Instructions" className="w-6 h-6" />
              </button>
            </div>
          </div>




            
            <div className="flex flex-col">
              <textarea
                id="instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                maxLength={8000}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
                rows="5"
                ref={(ref) => setInstructionsTextareaRef(ref)}
              ></textarea>
              
              <div className="mt-2 flex justify-between items-center">
                <div className="flex items-center">
                  <span className="text-gray-700 mr-2">Insert placeholder:</span>
                  <select
                    className="p-1 border rounded focus:outline-none focus:ring-2 focus:ring-[#0078D4] text-sm"
                    onChange={(e) => {
                      if (e.target.value) {
                        insertPlaceholder(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    defaultValue=""
                    style={{ maxHeight: '2.25rem' }}
                  >
                    <option value="" disabled>Select placeholder</option>
                    {availablePlaceholders.map((placeholder) => (
                      <option key={placeholder.name} value={placeholder.name} className="text-sm">
                        {placeholder.name} - {placeholder.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-sm text-gray-500">
                  <span 
                    className="inline-block w-4 h-4 text-center rounded-full border border-gray-500 cursor-help"
                    title="Use placeholders like {{username}} that will be replaced when the agent is used"
                  >
                    ?
                  </span>
                </div>



              </div>
              
              {instructions && (
                <div className="mt-3 p-2 border border-gray-200 rounded bg-gray-50">
                  <div className="flex justify-between items-center mb-1">
                    <div className="text-sm font-medium text-gray-700">Preview with highlighted placeholders:</div>
                    <button
                      onClick={() => setIsPreviewExpanded(!isPreviewExpanded)}
                      className="text-gray-500 hover:text-gray-700 focus:outline-none"
                      aria-label={isPreviewExpanded ? "Collapse preview" : "Expand preview"}
                    >
                      {isPreviewExpanded ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div 
                    className={`text-sm text-gray-800 overflow-hidden transition-all duration-300 ease-in-out ${
                      isPreviewExpanded ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    {highlightPlaceholders(instructions)}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="py-4"></div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Knowledge Sources (optional)</label>
            <form className="flex gap-2 mb-2" onSubmit={handleAddKnowledgeSource}>
              <select
                value={knowledgeSourceType}
                onChange={(e) => setKnowledgeSourceType(e.target.value)}
                className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
              >
                <option>SharePoint</option>
                <option>Web URL</option>
                <option>Document</option>
              </select>
              <input
                type="text"
                value={knowledgeSourceValue}
                onChange={(e) => setKnowledgeSourceValue(e.target.value)}
                placeholder="URL or path"
                className={`flex-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4] ${
                  knowledgeSourceError ? 'border-red-500' : ''
                }`}
              />
              <button
                type="submit"
                className="text-blue-500 hover:text-blue-700 px-3 py-1 border border-blue-500 rounded-lg"
              >
                Add
              </button>
            </form>
            {knowledgeSourceError && (
              <div className="text-xs text-red-600 mb-2">{knowledgeSourceError}</div>
            )}
            <div className="flex flex-wrap gap-2 mb-2">
              {knowledgeSourcePills.map((ks, index) => (
                <span
                  key={index}
                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                >
                  <span className="font-semibold">{ks.type}:</span> {ks.value}
                  <button
                    type="button"
                    onClick={() => handleRemoveKnowledgeSource(index)}
                    className="ml-2 text-blue-800 hover:text-red-600 font-bold"
                    aria-label="Remove knowledge source"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <div className="py-2"></div>
            <label className="block text-gray-700 mb-2">Capabilities (optional)</label>
            {availableCapabilities.map(cap => (
              <div key={cap} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={cap}
                  checked={selectedCapabilities.includes(cap)}
                  onChange={() => setSelectedCapabilities(prev =>
                    prev.includes(cap)
                      ? prev.filter(c => c !== cap)
                      : [...prev, cap]
                  )}
                />
                <label htmlFor={cap}>{cap}</label>
              </div>
            ))}
          </div>

          <div className="flex space-x-4">
            <button
              onClick={handleSubmit}
              type="button"
              className="flex-1 bg-gradient-to-r from-[#0078D4] to-[#00A4B4] text-white py-3 rounded-lg hover:scale-105 transition-transform"
            >
              Generate Agent Configuration
            </button>
            <button
              onClick={handleClear}
              type="button"
              className="w-1/3 bg-gray-300 text-gray-800 py-3 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Clear Form
            </button>
          </div>
        </div>
      </section>

      <FinalAgentModal
        isOpen={isFinalModalOpen}
        onClose={() => setIsFinalModalOpen(false)}
        agentData={finalAgentData}
      />

      <KeywordModal
        isOpen={isKeywordModalOpen}
        onClose={() => setIsKeywordModalOpen(false)}
        onDescriptionGenerated={handleDescriptionGenerated}
        useCase={useCase}
      />
    </>
  );
}

export default AgentForm;